'use client';

import { css } from 'styled-components';
import { Button, Placeholder } from '@lmn/design-system';
import { useTransportChangeFilters } from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersProvider';
import {
  FilterContainer,
  type FilterChange,
} from '@/app/components/hotel-detail/changeTransport/ChangeTransportFiltersCommon';
import ChangeFlightStopFilter from '@/app/components/hotel-detail/changeTransport/ChangeTransportStopFilter';
import ChangeTransportTimeSlotFilter from '@/app/components/hotel-detail/changeTransport/ChangeTransportTimeSlotFilter';
import ChangeTransportAirportsFilter from '@/app/components/hotel-detail/changeTransport/ChangeTransportAirportsFilter';
import ChangeTransportCompaniesFilter from '@/app/components/hotel-detail/changeTransport/ChangeTransportCompaniesFilter';
import { useCurrentFilterStatus } from '@/app/components/hotel-detail/CurrentFilterStatusProvider';
import { useTranslation } from '@/app/components/translations/Translation';
import ChangeTransportBaggageFilter from '@/app/components/hotel-detail/changeTransport/ChangeTransportBaggageFilter';
import { scrollToTop } from '@/app/common/utils/scroll.ts';

type ChangeTransportFilterProps = {
  onChangeFilterStart?: () => void;
  onChangeFilterEnd?: () => void;
} & React.HTMLAttributes<HTMLElement>;

export default function ChangeTransportFilters(props: ChangeTransportFilterProps) {
  const { currentFiltersStatus = undefined } = useCurrentFilterStatus();
  const { changeFilter } = useTransportChangeFilters();

  const onFilterChange = (filterChange: FilterChange) => changeFilter(filterChange);

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        row-gap: 1rem;
        line-height: ${(p) => p.theme.kallisto.font.lineHeight['250']};
      `}
      {...props}
    >
      {currentFiltersStatus && Object.keys(currentFiltersStatus).length === 0 ? <FiltersPlaceholders /> : undefined}
      {currentFiltersStatus ? (
        <>
          <ChangeTransportBaggageFilter
            baggageIncluded={currentFiltersStatus.baggageIncluded}
            handBaggageIncluded={currentFiltersStatus.handBaggageIncluded}
            onChange={onFilterChange}
          />
          <ChangeFlightStopFilter
            outboundStops={currentFiltersStatus.outboundStops}
            inboundStops={currentFiltersStatus.inboundStops}
            directOnly={currentFiltersStatus.directOnly}
            onChange={onFilterChange}
          />
          <ChangeTransportTimeSlotFilter
            outboundTimeSlots={currentFiltersStatus.outboundDepartureTimeSlots}
            inboundTimeSlots={currentFiltersStatus.inboundDepartureTimeSlots}
            onChange={onFilterChange}
          />
          <ChangeTransportAirportsFilter
            inboundArrivalAirports={
              currentFiltersStatus.inboundArrivalAirports || currentFiltersStatus.inboundArrivalStations
            }
            inboundDepartureAirports={
              currentFiltersStatus.inboundDepartureAirports || currentFiltersStatus.inboundDepartureStations
            }
            outboundArrivalAirports={
              currentFiltersStatus.outboundArrivalAirports || currentFiltersStatus.outboundArrivalStations
            }
            outboundDepartureAirports={
              currentFiltersStatus.outboundDepartureAirports || currentFiltersStatus.outboundDepartureStations
            }
            inboundSameAirport={currentFiltersStatus.inboundSameAirport || currentFiltersStatus.inboundSameStation}
            outboundSameAirport={currentFiltersStatus.outboundSameAirport || currentFiltersStatus.outboundSameStation}
            onChange={onFilterChange}
          />
          <ChangeTransportCompaniesFilter
            companies={currentFiltersStatus.airlines || currentFiltersStatus.companies}
            onChange={onFilterChange}
          />
        </>
      ) : null}
    </div>
  );
}

export function MobileTransportsFiltersModalFooter(props: { onResetClick: () => void; onApplyClick: () => void }) {
  const { t } = useTranslation();
  const { onResetClick, onApplyClick } = props;
  const resetAllTitle = t('vacation.common.filters.button.clear');
  const applyTitle = t('vacation.common.filters.button.apply');

  const onReset = () => {
    onResetClick();
    scrollToTop();
  };

  const onApply = () => {
    onApplyClick();
    scrollToTop();
  };

  return (
    <div
      css={`
        display: flex;
        column-gap: 16px;
      `}
    >
      <Button
        variant="primary"
        appearance="ghost"
        aria-label={resetAllTitle}
        onClick={onReset}
        css={`
          width: 50%;
        `}
      >
        {resetAllTitle}
      </Button>
      <Button
        variant="primary"
        aria-label={applyTitle}
        onClick={onApply}
        css={`
          width: 50%;
        `}
      >
        {applyTitle}
      </Button>
    </div>
  );
}

export function FiltersPlaceholders() {
  return (
    <div
      css={`
        display: flex;
        flex-direction: column;
        gap: 16px;
      `}
    >
      <Placeholder height="24px" width="120px" />
      <FilterContainer
        css={`
          gap: 16px;
          padding: 16px;
        `}
      >
        <Placeholder height="20px" width="80%" />
        <Placeholder height="20px" width="80%" />
      </FilterContainer>
      <Placeholder height="24px" width="100px" />
      <FilterContainer>
        <Placeholder height="20px" width="90%" />
      </FilterContainer>
      <FilterContainer
        css={`
          gap: 16px;
          padding: 16px;
        `}
      >
        <Placeholder height="20px" width="50%" />
        <Placeholder height="20px" width="100px" />
        <Placeholder height="20px" width="100px" />
        <Placeholder height="20px" width="120px" />
      </FilterContainer>
      <FilterContainer
        css={`
          gap: 16px;
          padding: 16px;
        `}
      >
        <Placeholder height="20px" width="50%" />
        <Placeholder height="20px" width="100px" />
        <Placeholder height="20px" width="100px" />
        <Placeholder height="20px" width="120px" />
      </FilterContainer>
      <Placeholder height="24px" width="50%" />
      <FilterContainer
        css={`
          gap: 12px;
        `}
      >
        <Placeholder height="20px" width="100px" />
        <Placeholder height="16px" width="140px" />
        <Placeholder height="20px" width="100%" />
        <Placeholder height="20px" width="100%" />
      </FilterContainer>
      <FilterContainer
        css={`
          gap: 12px;
        `}
      >
        <Placeholder height="20px" width="100px" />
        <Placeholder height="16px" width="140px" />
        <Placeholder height="20px" width="100%" />
        <Placeholder height="20px" width="100%" />
      </FilterContainer>
    </div>
  );
}
